import { useEffect, useState } from 'react';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Skeleton } from '@mui/material';
import { SplitButton, ThemeButton } from '@shared/ui/buttons';
import CardDisplay from '@widgets/cards/CardDisplay';
import Note from '@shared/ui/messages/Note';
import { ListData, Status } from '@shared/ui/parts';
import { useEditCardDialog, useTransferDialog } from '@utils/dialogs-hooks';
import { CardMenu } from '@widgets/cards/menu';
import { cardTypeLabels } from '@utils/static';
import { useTranslation } from 'react-i18next';
import { cardStatuses } from '@utils/statuses';
import { useCards } from '@app/api/cards/useCards';
import { useSelector } from 'react-redux';
import {
  formatBalanceUi,
  FRACTION_DIGITS,
  removeMinusFromBalance,
} from '@utils/balance-helpers';

export default function ViewCardDialog({ data, setSubtitle, setTitle }) {
  const [isLoadingData, setIsLoadingData] = useState(true);
  const { getCardDetails } = useCards();
  const [cardDetails, setCardDetails] = useState(null);
  const { t } = useTranslation();

  const { openEditCardDialog } = useEditCardDialog();
  const { openTransferDialog } = useTransferDialog();
  const isActiveCard = cardStatuses[cardDetails?.status] === 'active';
  const { bankWallets } = useSelector((state) => state.bankWallets);
  const binBalance =
    bankWallets.find((w) => w.bank_id === cardDetails?.details?.bank_id)
      ?.balance ?? cardDetails?.available_limit;
  const remainingLimit = Math.min(
    Number(cardDetails?.available_limit),
    Number(binBalance),
  );

  const listData = [
    {
      label: '3DS',
      value: cardDetails?._3ds,
      isHidden: true,
      isCopy: true,
    },
    {
      label: t('billing_address'),
      value: cardDetails?.billing_address || '-',
    },
    {
      label: t('charged'),
      value: removeMinusFromBalance(
        formatBalanceUi(cardDetails?.charged, cardDetails?.currency),
      ),
    },
    {
      label: t('on_hold'),
      value: removeMinusFromBalance(
        formatBalanceUi(cardDetails?.onHold, cardDetails?.currency),
      ),
    },
    {
      label: t('declined_transactions'),
      value: `${cardDetails?.declinedTransactionsCount} / ${cardDetails?.transactionsCount}`,
    },
    {
      label: t('transaction_limit'),
      value: formatBalanceUi(
        cardDetails?.transaction_limit,
        cardDetails?.currency,
        FRACTION_DIGITS.INTEGER,
      ),
    },
    {
      label: t('card_limit'),
      value: formatBalanceUi(
        cardDetails?.limit,
        cardDetails?.currency,
        FRACTION_DIGITS.INTEGER,
      ),
    },
  ];

  useEffect(() => {
    if (isLoadingData) return;
    if (!cardDetails) {
      setTitle('Card details');
      return;
    }
    const cardTypeKey = cardTypeLabels[cardDetails?.type];
    setSubtitle(<Status data={cardDetails} type="cards" />);
    setTitle(cardTypeKey ? `${cardTypeKey} card` : 'Card details');
  }, [cardDetails, isLoadingData]);

  useEffect(() => {
    async function fetchCardDetails() {
      try {
        const res = await getCardDetails(data);
        if (res) setCardDetails(res.card);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingData(false);
      }
    }

    fetchCardDetails();
  }, []);

  return (
    <>
      <DialogContent className="no-pd viewCard">
        <div className="form">
          <div className="customDialog-box-px">
            {isLoadingData ? (
              <Skeleton
                variant="text"
                height={60}
                sx={{ bgcolor: 'grey.100', transform: 'none' }}
              />
            ) : cardDetails ? (
              <>
                <p className="body-18m">{t('remaining_limit')}</p>
                <p
                  className="title-h1r"
                  dangerouslySetInnerHTML={{
                    __html: formatBalanceUi(
                      remainingLimit,
                      cardDetails?.currency,
                      null,
                      true,
                    ),
                  }}
                />
              </>
            ) : (
              <Note
                singleLine
                content={t('card_is_not_available')}
                type="error"
              />
            )}
          </div>
          {!isLoadingData && cardDetails && (
            <>
              <div className="customDialog__card">
                <CardDisplay data={cardDetails} />
              </div>
              {cardDetails?.note && (
                <div className="block-pd-x">
                  <Note
                    singleLine
                    icon="file"
                    type="info"
                    content={cardDetails?.note}
                  />
                </div>
              )}
              <div className="block-pd-x">
                <ListData listData={listData} />
              </div>
            </>
          )}
        </div>
      </DialogContent>
      {!isLoadingData && cardDetails && (
        <DialogActions>
          <div className="btns-toolbar">
            {isActiveCard ? (
              <ThemeButton
                onClick={() =>
                  openTransferDialog({
                    bank_id: cardDetails?.details?.bank_id,
                    bank_from: 0,
                  })
                }
                startIcon="transfer"
              >
                {t('Top Up')}
              </ThemeButton>
            ) : (
              <div className="w-full" />
            )}
            <SplitButton
              text={t('edit')}
              icon="edit"
              action={() => openEditCardDialog(cardDetails)}
              menuComponent={CardMenu}
              menuProps={{
                data: cardDetails,
                hideTopUpBin: isActiveCard,
                hideEdit: true,
              }}
            />
          </div>
        </DialogActions>
      )}
    </>
  );
}
